import React, { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import Pagination from "../components/Pagination";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import "../assets/css/theme-table-header.css";
import { listWhsales, deleteWhsale, isErpApiSuccess } from "../erpClient";
import { hasPermission, PERMISSIONS } from "../utils/permissions";
import useIsCurrentFinancialYear, {
  FY_ADD_DISABLED_TITLE,
} from "../hooks/useIsCurrentFinancialYear";
import {
  normalizeListWhsalesRows,
  isWhsalesApiSuccess,
  ERP_WHSALES_LIST_INVALIDATE_EVENT,
} from "../utils/whsalesErpAdapter";
import { parseApiPagination } from "../utils/parseApiPagination";
import { useVisitedPageCache, visitedPageKey } from "../utils/visitedPageCache";
import { Modal, Button } from "react-bootstrap";
import { formatDateToDDMMYYYY } from "./const";

function WholesaleInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCurrentFinancialYear = useIsCurrentFinancialYear();
  const { buttonClass, tableClass, cardClass } = useButtonTableStyle();
  const pageCache = useVisitedPageCache();

  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [canAdd, setCanAdd] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_ADD),
  );
  const [canEdit, setCanEdit] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_EDIT),
  );
  const [canDelete, setCanDelete] = useState(() =>
    hasPermission(PERMISSIONS.WHSALE_DELETE),
  );
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const sync = () => {
      setCanAdd(hasPermission(PERMISSIONS.WHSALE_ADD));
      setCanEdit(hasPermission(PERMISSIONS.WHSALE_EDIT));
      setCanDelete(hasPermission(PERMISSIONS.WHSALE_DELETE));
    };
    window.addEventListener("permissionsUpdated", sync);
    window.addEventListener("erpAuthUpdated", sync);
    return () => {
      window.removeEventListener("permissionsUpdated", sync);
      window.removeEventListener("erpAuthUpdated", sync);
    };
  }, []);

  const fetchList = useCallback(
    async (page = 1, limit = 10, search = "", force = false) => {
      const cacheKey = visitedPageKey([page, limit, search]);
      if (force) pageCache.clear();
      const cached = force ? null : pageCache.read(cacheKey);
      if (cached) {
        setRows(cached.items);
        setTotalCount(cached.total);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const res = await listWhsales({ search, page, limit });
        if (!isWhsalesApiSuccess(res) && !isErpApiSuccess(res)) {
          setRows([]);
          setTotalCount(0);
          return;
        }
        const list = normalizeListWhsalesRows(res);
        const total = parseApiPagination(res, list.length).total;
        setRows(list);
        setTotalCount(total);
        pageCache.write(cacheKey, { items: list, total });
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Failed to load wholesale bills");
        setRows([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (location.pathname !== "/whinvoice") return;
    fetchList(currentPage, itemsPerPage, searchQuery, true);
  }, [location.pathname]);

  useEffect(() => {
    const t = setTimeout(
      () => fetchList(currentPage, itemsPerPage, searchQuery),
      searchQuery ? 250 : 0,
    );
    return () => clearTimeout(t);
  }, [currentPage, itemsPerPage, searchQuery, fetchList]);

  useEffect(() => {
    const onInvalidate = () =>
      fetchList(currentPage, itemsPerPage, searchQuery, true);
    window.addEventListener(ERP_WHSALES_LIST_INVALIDATE_EVENT, onInvalidate);
    return () =>
      window.removeEventListener(ERP_WHSALES_LIST_INVALIDATE_EVENT, onInvalidate);
  }, [currentPage, itemsPerPage, searchQuery, fetchList]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteWhsale({ id: deleteTarget.id });
      if (isWhsalesApiSuccess(res) || isErpApiSuccess(res)) {
        toast.success("Wholesale bill deleted");
        setDeleteTarget(null);
        fetchList(currentPage, itemsPerPage, searchQuery, true);
        } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const remainingOf = (row) =>
    Number(row?.remaining_amount ?? row?.money_left ?? 0) || 0;

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className={`card w-100 position-relative overflow-hidden ${cardClass}`}>
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">Wh Sales</h4>
            {canAdd && (
                <button
                  className={`btn btn-primary ${buttonClass}`}
                disabled={!isCurrentFinancialYear}
                title={
                  !isCurrentFinancialYear ? FY_ADD_DISABLED_TITLE : undefined
                }
                onClick={() => {
                  if (!isCurrentFinancialYear) return;
                  navigate("/whsales");
                }}
              >
                Add Wholesale
                </button>
              )}
          </div>

          <div className="card-body p-4">
            <div className="d-flex align-items-center mb-3">
              <input
                type="text"
                className={`form-control ${buttonClass}`}
                style={{ maxWidth: "280px" }}
                placeholder="Search doc / customer..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={`table-responsive mb-4 border ${cardClass}`}>
              <table
                className={`table text-nowrap mb-0 align-middle theme-header-table ${tableClass}`}
              >
                <thead>
                  <tr>
                    <th className="px-2 py-2" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Doc No
                    </th>
                    <th className="px-2 py-2" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Date
                    </th>
                    <th className="px-2 py-2" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Customer
                    </th>
                    <th className="px-2 py-2" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Metal
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Out gm
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Old In gm
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Paid gm
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Baki gm
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Rate
                    </th>
                    <th className="px-2 py-2 text-end" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Total ₹
                    </th>
                    <th className="px-2 py-2 text-center" style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length > 0 ? (
                    rows.map((row) => (
                      <tr key={row.id} style={{ backgroundColor: "#ffffff" }}>
                        <td className="px-2 py-2" style={{ fontSize: "0.85rem" }}>
                          {row.doc_number || "-"}
                        </td>
                        <td className="px-2 py-2" style={{ fontSize: "0.85rem" }}>
                          {formatDateToDDMMYYYY(row.date) || row.date || "-"}
                        </td>
                        <td className="px-2 py-2" style={{ fontSize: "0.85rem" }}>
                          <div>{row.customer_name}</div>
                          {row.customer_mobile ? (
                            <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
                              {row.customer_mobile}
                            </div>
                          ) : null}
                          </td>
                        <td className="px-2 py-2" style={{ fontSize: "0.85rem" }}>
                          {row.metal_type}
                          </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {Number(row.total_out_gm || 0).toFixed(3)}
                          </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {Number(row.old_metal_in_gm || 0).toFixed(3)}
                          </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {Number(row.paid_gm || 0).toFixed(3)}
                        </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {Number(row.baki_gm || 0).toFixed(3)}
                        </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {Number(row.item_rate || 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-2 text-end" style={{ fontSize: "0.85rem" }}>
                          {formatIndianRupee(row.grand_total)}
                        </td>
                        <td className="px-2 py-2 text-center">
                          {canEdit && (
                            <button
                              className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                              title="Edit"
                              onClick={() =>
                                navigate("/editwhsale", {
                                  state: { whsaleId: row.id },
                                })
                              }
                            >
                              <Icon icon="solar:pen-bold" width="16" height="16" />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              className={`btn btn-primary btn-sm me-1 ${buttonClass}`}
                              title={
                                remainingOf(row) <= 0 &&
                                Number(row.baki_gm || 0) <= 0
                                  ? "Nothing pending"
                                  : "Settlement"
                              }
                              disabled={
                                remainingOf(row) <= 0 &&
                                Number(row.baki_gm || 0) <= 0
                              }
                              onClick={() =>
                                navigate("/whsale-settlement", {
                                  state: {
                                    whsaleId: row.id,
                                    settlementOnly: true,
                                  },
                                })
                              }
                            >
                              <Icon
                                icon="solar:wallet-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          {canDelete && (
                              <button
                              className={`btn btn-outline-danger btn-sm ${buttonClass}`}
                              title="Delete"
                              onClick={() => setDeleteTarget(row)}
                              >
                                <Icon
                                  icon="solar:trash-bin-trash-bold"
                                  width="16"
                                  height="16"
                                />
                              </button>
                            )}
                          </td>
                        </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" className="text-center">
                        No wholesale bills found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              totalItems={totalCount}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={(p) => setCurrentPage(p)}
              onItemsPerPageChange={(n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              }}
              itemsPerPageOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        </div>
      </div>

      <Modal
        show={Boolean(deleteTarget)}
        onHide={() => !deleting && setDeleteTarget(null)}
        centered
      >
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Delete wholesale?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Permanently delete{" "}
          <strong>{deleteTarget?.doc_number || deleteTarget?.id}</strong>?
        </Modal.Body>
        <Modal.Footer>
                          <Button
            variant="secondary"
            disabled={deleting}
            onClick={() => setDeleteTarget(null)}
          >
            Cancel
                          </Button>
          <Button variant="danger" disabled={deleting} onClick={confirmDelete}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default WholesaleInvoice;
